<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { Loader2 } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CartItemCard from '@/components/cart/v2/cart-item/CartItemCard.vue';
import DeleteErrorToast from '@/components/cart/v2/DeleteErrorToast.vue';
import CartPaymentFooter from '@/components/cart/v2/payment/CartPaymentFooter.vue';
import GuestLoginModal from '@/components/cart/v2/payment/GuestLoginModal.vue';
import PartialFailureActions from '@/components/cart/v2/payment/PartialFailureActions.vue';
import PartialFailureBanner from '@/components/cart/v2/payment/PartialFailureBanner.vue';
import PaymentConfirmationView from '@/components/cart/v2/payment/PaymentConfirmationView.vue';
import PaymentModal from '@/components/cart/v2/payment/PaymentModal.vue';
import { useCartPaymentFlow } from '@/composables/carts/useCartPaymentFlow';
import { useContactFieldsCompletion } from '@/composables/carts/useContactFieldsCompletion';
import { useModeCart } from '@/composables/carts/useModeCart';
import { usePartialFailure } from '@/composables/carts/usePartialFailure';
import { createCartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { buildGA4ItemsFromCart, computeCartValue, pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';

const { globalConfiguration } = storeToRefs(useGlobalConfigurationStore());

const cartsTransactionsApi = createCartsTransactionsApi();
if (globalConfiguration.value?.apikey) {
  cartsTransactionsApi.setApiKey(globalConfiguration.value.apikey);
}

const { t } = useI18n();

const {
  cartDetails,
  enrichedCartItems,
  deletingItemId,
  deleteError,
  totalPrice,
  hasNoCart,
  isLoadingCart,
  hasOnlyExpiredItems,
  terminalCartState,
  loadCartDetails,
  removeCartItem,
  clearDeleteError,
  formatDateTime,
  getEnrichedUnits,
  isItemExpired,
} = useModeCart(cartsTransactionsApi);

const { handleContactFieldsCompleted, isPaymentBlocked } = useContactFieldsCompletion(
  () => enrichedCartItems.value,
  isItemExpired,
);

const {
  failedBookingIds,
  isPartialFailure,
  isAllFailedDueToExpiration,
  isProcessingDecision,
  confirmedItemsTotal,
  decisionPaymentStatus,
  isItemFailed,
  handlePartialFailure,
  sendPartialDecision,
  resetPartialFailureState,
} = usePartialFailure(cartsTransactionsApi, cartDetails, isItemExpired);

const {
  guestLoginModalRef,
  paymentModalRef,
  isPaymentCompleted,
  completedReferenceNumber,
  handlePaymentClick,
  handleResumePayment,
  handleCustomerCreated,
  handlePaymentCompleted,
  markAsCompleted,
} = useCartPaymentFlow(cartsTransactionsApi, cartDetails);

const hasPendingTransaction = computed(() => cartDetails.value?.state === 'PENDING');

function isDeleting(itemId: string): boolean {
  return deletingItemId.value === itemId;
}

watch(decisionPaymentStatus, (status) => {
  if (status === 'captured') {
    markAsCompleted(cartDetails.value?.referenceNumber ?? null);
    resetPartialFailureState();
  }

  if (status === 'failed') {
    resetPartialFailureState();
    hasNoCart.value = true;
  }
});

onMounted(async () => {
  await loadCartDetails();

  if (cartDetails.value && !terminalCartState.value) {
    pushGA4EcommerceEvent('view_cart', {
      currency: cartDetails.value.currency,
      value: computeCartValue(cartDetails.value),
      items: buildGA4ItemsFromCart(cartDetails.value),
    });
  }

  if (terminalCartState.value === 'COMPLETED') {
    markAsCompleted(cartDetails.value?.referenceNumber ?? null);
    return;
  }

  guestLoginModalRef.value?.loadExistingCustomer();

  if (cartDetails.value?.state === 'APPROVED') {
    cartsTransactionsApi.fetchPartialFailureInfo().then((partialFailureInfo) => {
      if (partialFailureInfo) {
        handlePartialFailure({
          failedBookingIds: partialFailureInfo.failedBookingIds,
          failureReasons: partialFailureInfo.failureReasons,
        });
      }
    });
  }
});

const handleItemExpired = useDebounceFn(() => {
  loadCartDetails();
}, 500);

async function handlePaymentFailed() {
  await loadCartDetails();
}
</script>

<template>
  <div class="yb-preset">
    <PaymentConfirmationView v-if="isPaymentCompleted && completedReferenceNumber" :reference-number="completedReferenceNumber" />

    <template v-else>
      <div class="cart-layout">
        <div class="cart-details-container">
          <PartialFailureBanner v-if="isPartialFailure" :failed-count="failedBookingIds.length" :is-expiration="isAllFailedDueToExpiration" />

          <div v-if="isLoadingCart" class="cart-loading">
            <Loader2 :size="32" class="yb-spinner" aria-hidden="true" />
            <p>{{ t('cart.details.loading') }}</p>
          </div>
          <div v-else-if="enrichedCartItems.length" class="cart-item">
            <CartItemCard
              v-for="item in enrichedCartItems"
              :key="item.itemId"
              :item="item"
              :enriched-units="getEnrichedUnits(item)"
              :currency="cartDetails?.currency"
              :currency-precision="cartDetails?.currencyPrecision"
              :date-time="item.bookingDetails ? formatDateTime(item.bookingDetails.sessionLocalDateTimeStart) : ''"
              :is-deleting="isDeleting(item.itemId)"
              :is-expired="isItemExpired(item)"
              :is-partial-failure="isPartialFailure"
              :is-item-failed="isItemFailed(item)"
              :has-pending-transaction="hasPendingTransaction"
              :carts-transactions-api="cartsTransactionsApi"
              @delete="removeCartItem(item.itemId)"
              @expired="handleItemExpired"
              @update:contact-fields-completed="(value: boolean) => handleContactFieldsCompleted(item.itemId, value)"
            />
          </div>
          <div v-else>
            <p>
              {{ hasNoCart ? t('cart.empty') : t('cart.details.empty') }}
            </p>
          </div>

          <DeleteErrorToast
            :error-message="deleteError?.message ?? null"
            @clear="clearDeleteError"
          />
        </div>

        <PartialFailureActions
          v-if="isPartialFailure && enrichedCartItems.length"
          :is-processing-decision="isProcessingDecision"
          :confirmed-items-total="confirmedItemsTotal"
          @decide="sendPartialDecision"
        />

        <CartPaymentFooter
          v-else-if="enrichedCartItems.length"
          :total-price="totalPrice"
          :has-only-expired-items="hasOnlyExpiredItems"
          :has-pending-transaction="hasPendingTransaction"
          :is-payment-blocked="isPaymentBlocked"
          @pay="handlePaymentClick"
          @resume="handleResumePayment"
        />
      </div>

      <GuestLoginModal
        ref="guestLoginModalRef"
        :carts-transactions-api="cartsTransactionsApi"
        :required-customer-fields="cartDetails?.requiredCustomerFields ?? []"
        :cart-items="enrichedCartItems"
        @customer-created="handleCustomerCreated"
      />

      <PaymentModal
        ref="paymentModalRef"
        :carts-transactions-api="cartsTransactionsApi"
        @payment-completed="handlePaymentCompleted"
        @payment-failed="handlePaymentFailed"
        @partial-failure="handlePartialFailure"
        @transaction-started="loadCartDetails"
      />
    </template>
  </div>
</template>

<style scoped lang="css">
.yb-preset {
  width: 100%;
}

.cart-layout {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}

.cart-details-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--yb-spacing-2xl);
}

.cart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--yb-spacing-base);
  padding: var(--yb-spacing-2xl) 0;
  color: var(--yb-color-darkgray);
}

.cart-item {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}
</style>
